import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import NetWorthChart from '../../charts/NetWorthChart';
import ChartErrorBoundary from '../../charts/ChartErrorBoundary';
import { SimplifiedRetirementFinancialInputs } from '../../Types/Autogenerated/SimplifiedRetirementFinancialInputs';
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { SimplifiedRetirementCalculator } from '../../Utils/SimplifiedRetirementCalculator';
import TermsDescriptions from '../../components/TermsDescriptions';
import { netWorthTerms } from '../../Utils/TermDefinitions';
import FloatingChartButton from '../../components/FloatingChartButton';
import { useDebounce } from '../../hooks/useDebounce';
import ChartSummary from '../../components/ChartSummary';
import { generateNetWorthSummary } from '../../Utils/ChartSummaryGenerators';
import { formatCurrency } from '../../Utils/chartUtils';


interface NetWorthPageProps {
  formData: FormData;
  selectedPage: string;
  personsToggle: boolean;
  adjustYearlyForInflationToggle: boolean;
  showSnackbar: boolean;
  snackbarMessage: string;
  chartData: number[][] | null;
  handleClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
  setChartData: (value: React.SetStateAction<number[][] | null>) => void;
  setSnackbarMessage: (value: React.SetStateAction<string>) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
  handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const NetWorthPage: React.FC<NetWorthPageProps> = ({
  formData,
  selectedPage,
  personsToggle,
  adjustYearlyForInflationToggle,
  showSnackbar,
  snackbarMessage,
  chartData,
  handleClose,
  setChartData,
  setSnackbarMessage,
  handleInputChange,
  onValueChange,
  handleToggleChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Debounce form data to reduce chart update frequency
  const debouncedFormData = useDebounce(formData, 300); // 300ms delay

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleFinancialInputChange = (value: string, name: string) => {
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    onValueChange(numericValue.toString(), name);
  };


  const calculateNetWorth = useCallback(() => {
    if (isLoading) return; // Prevent multiple simultaneous calculations
    
    setIsLoading(true);
    try {
      console.log('NetWorth Debug - Form Data:', {
        personsToggle,
        currentAge: formData.currentAge,
        currentAgePerson1: formData.currentAgePerson1,
        contributionsEndAge: formData.contributionsEndAge,
        contributionsEndAgePerson1: formData.contributionsEndAgePerson1,
        initialInvestments: formData.initialInvestmentsBalance,
        yearlyContributions: formData.yearlyInvestmentsContribution
      });
      const retirementFinancialInputs: SimplifiedRetirementFinancialInputs[] = personsToggle
        ? [
            {
              initial_investments_balance: parseFloat(debouncedFormData.initialInvestmentsBalancePerson1),
              yearly_investments_contribution: parseFloat(debouncedFormData.yearlyInvestmentsContributionPerson1),
              contributions_end_age: parseInt(debouncedFormData.contributionsEndAgePerson1, 10),
              withdrawal_start_age: parseInt(debouncedFormData.contributionsEndAgePerson1, 10), // Use contributions end age since we don't have withdrawal age for net worth
              current_age: parseInt(debouncedFormData.currentAgePerson1, 10),
            },
            {
              initial_investments_balance: parseFloat(debouncedFormData.initialInvestmentsBalancePerson2),
              yearly_investments_contribution: parseFloat(debouncedFormData.yearlyInvestmentsContributionPerson2),
              contributions_end_age: parseInt(debouncedFormData.contributionsEndAgePerson2, 10),
              withdrawal_start_age: parseInt(debouncedFormData.contributionsEndAgePerson2, 10), // Use contributions end age since we don't have withdrawal age for net worth
              current_age: parseInt(debouncedFormData.currentAgePerson2, 10),
            }
          ]
        : [
            {
              initial_investments_balance: parseFloat(debouncedFormData.initialInvestmentsBalance),
              yearly_investments_contribution: parseFloat(debouncedFormData.yearlyInvestmentsContribution),
              contributions_end_age: parseInt(debouncedFormData.contributionsEndAge, 10),
              withdrawal_start_age: parseInt(debouncedFormData.contributionsEndAge, 10), // Use contributions end age since we don't have withdrawal age for net worth
              current_age: parseInt(debouncedFormData.currentAge, 10),
            }
          ];

      const personsData: number[][] = [];

      retirementFinancialInputs.forEach((input, index) => {
        console.log(`NetWorth Debug - Person ${index + 1} Input:`, input);
        const calculator = new SimplifiedRetirementCalculator(
          input,
          adjustYearlyForInflationToggle,
          parseFloat(debouncedFormData.averageAnnualReturn) / 100,
          parseFloat(debouncedFormData.inflationRate) / 100,
          0, // No withdrawal rate for net worth calculation
        );
        const timeSeriesData = calculator.compute_retirement_savings_timeseries("BROKERAGE");
        console.log(`NetWorth Debug - Person ${index + 1} Time Series Data:`, timeSeriesData);
        personsData.push(timeSeriesData);
      });

      setChartData(personsData);
    } finally {
      setIsLoading(false);
    }
  }, [
    personsToggle,
    debouncedFormData,
    adjustYearlyForInflationToggle,
    setChartData,
    isLoading,
    formData.contributionsEndAge,
    formData.contributionsEndAgePerson1,
    formData.currentAge,
    formData.currentAgePerson1,
    formData.initialInvestmentsBalance,
    formData.yearlyInvestmentsContribution
  ]);


  // Auto-calculate whenever any input changes
  useEffect(() => {
    try {
      calculateNetWorth();
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }, [calculateNetWorth, setSnackbarMessage]);

  return (
    <div className="net-worth-calculator w-full px-2 sm:px-4 lg:px-6 financial-app">
      {/* Professional Header */}
      <div className="hero-section mb-6 -mx-2 sm:-mx-4 lg:-mx-6 px-2 sm:px-4 lg:px-6">
        <div className="professional-layout">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 tracking-tight">
            {selectedPage}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Track your net worth growth over time with detailed savings projections
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Form Section */}
        <div className="lg:w-2/5 min-w-0">
          <Card className="w-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                <h2 className={cn("text-xl font-semibold text-card-foreground")}>Investment Calculator</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Configure your investment parameters and track net worth growth
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Investment Configuration */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className={cn("flex items-center justify-between")}>
                  <div>
                    <Label htmlFor="personsToggle" className="text-sm font-medium text-foreground">
                      Multiple Investment Sources
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Calculate for dual investment households
                    </p>
                  </div>
                  <Switch
                    id="personsToggle"
                    checked={personsToggle}
                    onCheckedChange={(checked) => handleToggleChange({
                      target: { name: 'personsToggle', checked, id: 'persons', type: 'checkbox' }
                    } as React.ChangeEvent<HTMLInputElement>)}
                  />
                </div>
              </div>

              {/* Investment Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Investment Details</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className={cn("grid gap-6", personsToggle ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
                  <div className={cn("space-y-4")}>
                    <div className="space-y-2">
                      <Label htmlFor={personsToggle ? "initialInvestmentsBalancePerson1" : "initialInvestmentsBalance"} className="text-sm font-medium text-foreground flex items-center gap-2">
                        {personsToggle ? "Primary Current Investments" : "Current Investment Balance"}
                        <span className="text-xs text-muted-foreground">(today's value)</span>
                      </Label>
                      <div className={cn("relative group")}>
                        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                        <Input
                          id={personsToggle ? "initialInvestmentsBalancePerson1" : "initialInvestmentsBalance"}
                          name={personsToggle ? "initialInvestmentsBalancePerson1" : "initialInvestmentsBalance"}
                          type="text"
                          placeholder="50,000"
                          value={formatNumber(personsToggle ? formData.initialInvestmentsBalancePerson1 : formData.initialInvestmentsBalance)}
                          onChange={(e) => handleFinancialInputChange(e.target.value, personsToggle ? 'initialInvestmentsBalancePerson1' : 'initialInvestmentsBalance')}
                          className={cn("pl-8 text-lg font-medium")}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={personsToggle ? "yearlyInvestmentsContributionPerson1" : "yearlyInvestmentsContribution"} className="text-sm font-medium text-foreground flex items-center gap-2">
                        {personsToggle ? "Primary Annual Savings" : "Annual Savings Amount"}
                        <span className="text-xs text-muted-foreground">(per year)</span>
                      </Label>
                      <div className={cn("relative group")}>
                        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                        <Input
                          id={personsToggle ? "yearlyInvestmentsContributionPerson1" : "yearlyInvestmentsContribution"}
                          name={personsToggle ? "yearlyInvestmentsContributionPerson1" : "yearlyInvestmentsContribution"}
                          type="text"
                          placeholder="12,000"
                          value={formatNumber(personsToggle ? formData.yearlyInvestmentsContributionPerson1 : formData.yearlyInvestmentsContribution)}
                          onChange={(e) => handleFinancialInputChange(e.target.value, personsToggle ? 'yearlyInvestmentsContributionPerson1' : 'yearlyInvestmentsContribution')}
                          className={cn("pl-8 text-lg font-medium")}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={personsToggle ? "currentAgePerson1" : "currentAge"} className="text-sm font-medium text-foreground">
                        {personsToggle ? "Primary Current Age" : "Your Current Age"}
                      </Label>
                      <Input
                        id={personsToggle ? "currentAgePerson1" : "currentAge"}
                        name={personsToggle ? "currentAgePerson1" : "currentAge"}
                        type="number"
                        placeholder="30"
                        value={personsToggle ? formData.currentAgePerson1 : formData.currentAge}
                        onChange={handleInputChange}
                        className={cn("text-lg font-medium")}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={personsToggle ? "contributionsEndAgePerson1" : "contributionsEndAge"} className="text-sm font-medium text-foreground">
                        {personsToggle ? "Primary Stop Saving Age" : "Stop Saving Age"}
                      </Label>
                      <Input
                        id={personsToggle ? "contributionsEndAgePerson1" : "contributionsEndAge"}
                        name={personsToggle ? "contributionsEndAgePerson1" : "contributionsEndAge"}
                        type="number"
                        placeholder="65"
                        value={personsToggle ? formData.contributionsEndAgePerson1 : formData.contributionsEndAge}
                        onChange={handleInputChange}
                        className={cn("text-lg font-medium")}
                      />
                    </div>
                  </div>

                  {personsToggle && (
                    <div className={cn("space-y-4 border-l-2 border-border pl-4")}>
                      <div className="space-y-2">
                        <Label htmlFor="initialInvestmentsBalancePerson2" className="text-sm font-medium text-foreground flex items-center gap-2">
                          Secondary Current Investments
                          <span className="text-xs text-muted-foreground">(today's value)</span>
                        </Label>
                        <div className={cn("relative group")}>
                          <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                          <Input
                            id="initialInvestmentsBalancePerson2"
                            name="initialInvestmentsBalancePerson2"
                            type="text"
                            placeholder="30,000"
                            value={formatNumber(formData.initialInvestmentsBalancePerson2)}
                            onChange={(e) => handleFinancialInputChange(e.target.value, 'initialInvestmentsBalancePerson2')}
                            className={cn("pl-8 text-lg font-medium")}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="yearlyInvestmentsContributionPerson2" className="text-sm font-medium text-foreground flex items-center gap-2">
                          Secondary Annual Savings
                          <span className="text-xs text-muted-foreground">(per year)</span>
                        </Label>
                        <div className={cn("relative group")}>
                          <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                          <Input
                            id="yearlyInvestmentsContributionPerson2"
                            name="yearlyInvestmentsContributionPerson2"
                            type="text"
                            placeholder="8,000"
                            value={formatNumber(formData.yearlyInvestmentsContributionPerson2)}
                            onChange={(e) => handleFinancialInputChange(e.target.value, 'yearlyInvestmentsContributionPerson2')}
                            className={cn("pl-8 text-lg font-medium")}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="currentAgePerson2" className="text-sm font-medium text-foreground">
                          Secondary Current Age
                        </Label>
                        <Input
                          id="currentAgePerson2"
                          name="currentAgePerson2"
                          type="number"
                          placeholder="28"
                          value={formData.currentAgePerson2}
                          onChange={handleInputChange}
                          className={cn("text-lg font-medium")}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contributionsEndAgePerson2" className="text-sm font-medium text-foreground">
                          Secondary Stop Saving Age
                        </Label>
                        <Input
                          id="contributionsEndAgePerson2"
                          name="contributionsEndAgePerson2"
                          type="number"
                          placeholder="62"
                          value={formData.contributionsEndAgePerson2}
                          onChange={handleInputChange}
                          className={cn("text-lg font-medium")}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Parameters Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Financial Parameters</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className="p-4 rounded-lg bg-muted/30 border border-border">
                  <div className={cn("flex items-center justify-between")}>
                    <div>
                      <Label htmlFor="adjustYearlyForInflationToggle" className="text-sm font-medium text-foreground">
                        Inflation-Adjusted Contributions
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Increase annual savings to keep pace with inflation
                      </p>
                    </div>
                    <Switch
                      id="adjustYearlyForInflationToggle"
                      checked={adjustYearlyForInflationToggle}
                      onCheckedChange={(checked) => handleToggleChange({
                        target: { name: 'adjustYearlyForInflationToggle', checked, id: 'adjustYearlyForInflationToggle', type: 'checkbox' }
                      } as React.ChangeEvent<HTMLInputElement>)}
                    />
                  </div>
                </div>
                
                <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                  <div className="space-y-2">
                    <Label htmlFor="inflationRate" className="text-sm font-medium text-foreground">Expected Inflation Rate</Label>
                    <div className={cn("relative group")}>
                      <Input
                        id="inflationRate"
                        name="inflationRate"
                        type="number"
                        placeholder="3.0"
                        value={formData.inflationRate?.toString() || ''}
                        onChange={handleInputChange}
                        className={cn("pr-8")}
                      />
                      <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>%</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="averageAnnualReturn" className="text-sm font-medium text-foreground">Expected Annual Return</Label>
                    <div className={cn("relative group")}>
                      <Input
                        id="averageAnnualReturn"
                        name="averageAnnualReturn"
                        type="number"
                        placeholder="7.0"
                        value={formData.averageAnnualReturn?.toString() || ''}
                        onChange={handleInputChange}
                        className={cn("pr-8")}
                      />
                      <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Chart Section */}
        <div className="lg:w-3/5 min-w-0" ref={chartRef}>
          <Card className="w-full h-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-accent to-primary rounded-full"></div>
                  <div>
                    <h2 className="text-xl font-semibold text-card-foreground">Net Worth Projection</h2>
                    <p className="text-sm text-muted-foreground">
                      Your investment growth over time
                    </p>
                  </div>
                </div>
                {chartData && !isLoading && (
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Projected Balance</div>
                    <div className="text-2xl font-bold text-primary">
                      {formatCurrency(
                        chartData.reduce((sum, personData) => {
                          const lastValue = personData[personData.length - 1] || 0;
                          return sum + lastValue;
                        }, 0)
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="h-full">
              {chartData && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow">
                    <ChartErrorBoundary>
                      <NetWorthChart 
                        timeSeriesData={chartData} 
                        contributionsEndAge={formData.contributionsEndAge}
                        contributionsEndAgePerson1={formData.contributionsEndAgePerson1}
                        contributionsEndAgePerson2={formData.contributionsEndAgePerson2}
                        currentAge={formData.currentAge}
                        currentAgePerson1={formData.currentAgePerson1}
                        currentAgePerson2={formData.currentAgePerson2}
                        personsToggle={personsToggle} 
                      />
                    </ChartErrorBoundary>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-4">
                    {isLoading && (
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/10 to-accent/10"></div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-lg font-medium text-foreground mb-1">
                        {isLoading ? 'Calculating your net worth...' : 'Ready to calculate'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isLoading ? 'Processing investment projections' : 'Enter your investment details to see projections'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Chart Summary Section */}
      {chartData && !isLoading && (
        <div className="mt-6">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-success to-primary rounded-full"></div>
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground">Investment Analysis</h2>
                  <p className="text-sm text-muted-foreground">
                    Key insights from your net worth projection
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ChartSummary 
                summaryItems={generateNetWorthSummary(chartData, {
                  yearlyContribution: personsToggle 
                    ? parseFloat(formData.yearlyInvestmentsContributionPerson1) + parseFloat(formData.yearlyInvestmentsContributionPerson2)
                    : parseFloat(formData.yearlyInvestmentsContribution),
                  growWithInflation: adjustYearlyForInflationToggle,
                  averageAnnualReturn: parseFloat(formData.averageAnnualReturn),
                  inflationRate: parseFloat(formData.inflationRate)
                })}
                title=""
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Professional Terms Section */}
      <div className="mt-6">
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-muted-foreground to-secondary rounded-full"></div>
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Understanding Net Worth</h2>
                <p className="text-sm text-muted-foreground">
                  Learn about the key concepts in net worth calculation
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <TermsDescriptions 
              terms={netWorthTerms} 
              title=""
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Enhanced Error Display */}
      {showSnackbar && (
        <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="bg-destructive text-destructive-foreground p-4 rounded-lg shadow-lg border border-destructive/20 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-destructive-foreground rounded-full"></div>
              <div className="font-medium">Calculation Error</div>
            </div>
            <div className="mt-1 text-sm opacity-90">
              {snackbarMessage}
            </div>
          </div>
        </div>
      )}
      
      <FloatingChartButton
        chartRef={chartRef}
        chartData={chartData}
      />
    </div>
  );
};

export default NetWorthPage;